import './CreateTrader.scss';
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useProtectedInterval } from '../../hooks/useProtectedInterval';
import EmptyState from '../../components/Layout/EmptyState';
import Toast from '../../components/Toast/Toast';
import { getApiErrorMessage } from '../../utils/apiErrors';

const STATUS_LABELS = {
    new: { text: 'Ожидает', className: 'CreateTrader__status--new' },
    approved: { text: 'Создан', className: 'CreateTrader__status--approved' },
    canceled: { text: 'Отменена', className: 'CreateTrader__status--canceled' },
};

export default function CreateTrader() {
    const { t } = useTranslation('layout');
    const api_url = useSelector((state) => state.API_URL);

    const [requests, setRequests] = useState([]);
    const [balance, setBalance] = useState(0);
    const [depositCost, setDepositCost] = useState(100);
    const [requireApproval, setRequireApproval] = useState(true);
    const [loading, setLoading] = useState(true);

    const [username, setUsername] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState(null);

    const fetchRequests = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        if (!jwtToken) return;

        try {
            const response = await fetch(api_url + 'teamlead/trader-requests/', {
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
            });
            if (response.ok) {
                const data = await response.json();
                setRequests(data.requests || []);
                setBalance(data.teamlead_balance || 0);
                // Zero is a valid configured price: only fall back when the API
                // did not provide the field at all.
                setDepositCost(data.trader_deposit_usdt ?? 100);
                setRequireApproval(data.require_approval);
            }
        } catch (e) {
            console.error('Failed to fetch trader requests:', e);
        } finally {
            setLoading(false);
        }
    }, [api_url]);

    useProtectedInterval(fetchRequests, [api_url]);

    const handleCreate = async () => {
        if (!username.trim() || username.trim().length < 3) {
            setToast({ type: 'error', message: 'Username должен быть не менее 3 символов' });
            return;
        }

        setSubmitting(true);
        const jwtToken = localStorage.getItem('accessToken');

        try {
            const response = await fetch(api_url + 'teamlead/create-trader/', {
                method: 'POST',
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ trader_username: username.trim() }),
            });
            const data = await response.json().catch(() => null);
            if (response.ok) {
                setToast({ type: 'success', message: data.message });
                setUsername('');
                fetchRequests();
            } else {
                setToast({ type: 'error', message: getApiErrorMessage(data) });
            }
        } catch (e) {
            setToast({ type: 'error', message: 'Ошибка сети' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async (id) => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(api_url + `teamlead/trader-requests/${id}/cancel/`, {
                method: 'POST',
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
            });
            const data = await response.json().catch(() => null);
            if (response.ok) {
                setToast({ type: 'success', message: data.message });
                fetchRequests();
            } else {
                setToast({ type: 'error', message: getApiErrorMessage(data) });
            }
        } catch (e) {
            setToast({ type: 'error', message: 'Ошибка сети' });
        }
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return d.toLocaleDateString('ru-RU') + ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return <div style={{ padding: '24px' }}><h2>{t('sidebar.menu.createTrader')}</h2><p>Загрузка...</p></div>;
    }

    return (
        <div className="CreateTrader">
            {toast && <Toast show type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

            <h2>{t('sidebar.menu.createTrader')}</h2>

            {/* Форма создания */}
            <div className="CreateTrader__form-card">
                <div className="CreateTrader__form-info">
                    <span>Ваш баланс: <b>{balance} $</b></span>
                    <span>Стоимость создания: <b>{depositCost} $</b></span>
                    {requireApproval && <span className="CreateTrader__approval-note">Требуется одобрение администратора</span>}
                </div>

                <div className="CreateTrader__form-row">
                    <input
                        className="CreateTrader__input"
                        type="text"
                        placeholder="Username нового трейдера"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !submitting && handleCreate()}
                        disabled={submitting}
                    />
                    <button
                        className="CreateTrader__submit"
                        onClick={handleCreate}
                        disabled={submitting || !username.trim() || balance < depositCost}
                    >
                        {submitting ? 'Создание...' : 'Создать'}
                    </button>
                </div>

                {balance < depositCost && (
                    <p className="CreateTrader__warning">Недостаточно средств для создания трейдера</p>
                )}
            </div>

            {/* Список заявок */}
            <h3 className="CreateTrader__subtitle">Заявки на создание</h3>

            {requests.length === 0 ? (
                <EmptyState message="Нет заявок на создание трейдеров" />
            ) : (
                <div className="CreateTrader__table-wrap">
                    <table className="CreateTrader__table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Депозит</th>
                                <th>Статус</th>
                                <th>Дата</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map(r => {
                                const status = STATUS_LABELS[r.status] || { text: r.status, className: '' };
                                return (
                                    <tr key={r.id}>
                                        <td className="CreateTrader__username">{r.trader_username}</td>
                                        <td>{r.deposit_amount} $</td>
                                        <td><span className={`CreateTrader__status ${status.className}`}>{status.text}</span></td>
                                        <td className="CreateTrader__date">{formatDate(r.created)}</td>
                                        <td>
                                            {r.status === 'new' && (
                                                <button className="CreateTrader__cancel-btn" onClick={() => handleCancel(r.id)}>
                                                    Отменить
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
